import { shallowRef } from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useConnectionStore } from '@/app/stores/connection';
import { useUiStore } from '@/app/stores/ui';
import { analytics } from '@/services/Analytics';
import { animations } from '@/services/Animations';
import { restApi } from '@/services/api';
import { Http } from '@/services/api/http';
import { authConfig } from '@/services/auth/auth-config';
import { idleWatcher } from '@/services/IdleWatcher';
import { kioskConfig } from '@/services/KioskConfig';
import { kioskHardware } from '@/services/KioskHardware';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';
import { messageOf } from '@/shared/utils/track-telemetry';
import type { AppBoundHandlers, AppLifecycleRuntime } from './appRuntimeTypes';

function warn(...args: unknown[]): void {
  // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
  console.warn(...args);
}

export function useAppLifecycle(): AppLifecycleRuntime {
  const ui = useUiStore();
  const connection = useConnectionStore();

  Http.setConnectionStateHooks({
    onRetryStarted: () => connection.setReconnecting(),
    onRequestSettled: () => connection.setOnline(),
  });

  const boundHandlers: AppBoundHandlers = {
    beforeUnload: null,
    localeChanged: null,
    pagehide: null,
    visibilityChange: null,
  };
  const tabHiddenTimestamp = shallowRef<number | null>(null);

  // The analytics queue sends at the end of a 15 s window. A phone does not
  // fire beforeunload, and the system can close a hidden page with no other
  // event, thus pagehide and a hidden page also send the queue.
  function flushAnalyticsOnLeave(): void {
    if (analytics.isFlushInProgress) {
      return;
    }
    analytics.flushQueuedMetricsOnUnload({
      isCustomerFacingPage: router.isCustomerFacingPage,
      apiKey: authConfig.getApiKey(),
    });
  }

  function initializeApp(
    exposePublicApi: () => void,
    initializeKioskServices: () => void
  ): void {
    exposePublicApi();

    try {
      sentryService.addBreadcrumb(
        'app',
        'App.vue mounted - initialization started',
        {
          isStepPage: router.isStepPage,
          isCustomerFacingPage: router.isCustomerFacingPage,
          slug: router.slug || null,
        }
      );
    } catch (error) {
      warn('[Sentry] App mount breadcrumb failed:', messageOf(error));
    }

    boundHandlers.beforeUnload = (): void => {
      if (session.id) {
        try {
          sentryService.endSentrySession('abnormal');
        } catch (error) {
          warn(
            '[App] Sentry session end failed on beforeunload:',
            messageOf(error)
          );
        }
      }

      flushAnalyticsOnLeave();
    };
    window.addEventListener('beforeunload', boundHandlers.beforeUnload);

    boundHandlers.pagehide = (event: PageTransitionEvent): void => {
      if (session.id && event.persisted === false) {
        // session.end() never runs on a reload or close. A customer page can
        // hold the kiosk's session id, and it must not cancel the kiosk.
        if (!router.isCustomerFacingPage) {
          restApi.sendGenerationCancel(session.id);
        }

        try {
          sentryService.endSentrySession('abnormal');
          sentryService.flush(500);
        } catch (error) {
          warn(
            '[App] Sentry session end failed on pagehide:',
            messageOf(error)
          );
        }
      }
      flushAnalyticsOnLeave();
    };
    window.addEventListener('pagehide', boundHandlers.pagehide);

    boundHandlers.visibilityChange = (): void => {
      try {
        if (document.visibilityState === 'hidden') {
          tabHiddenTimestamp.value = Date.now();
          flushAnalyticsOnLeave();
          sentryService.addBreadcrumb('app', 'Tab hidden', {
            sessionId: session.id || null,
          });
        } else if (tabHiddenTimestamp.value && session.id) {
          const hiddenDuration = Date.now() - tabHiddenTimestamp.value;
          const fiveMinutes = 5 * 60 * 1000;

          sentryService.addBreadcrumb('app', 'Tab visible', {
            hiddenDurationMs: hiddenDuration,
            sessionId: session.id || null,
          });

          if (hiddenDuration > fiveMinutes) {
            sentryService.addBreadcrumb(
              'app',
              'Extended tab-away detected, resetting Sentry session',
              {
                hiddenDurationMs: hiddenDuration,
                threshold: fiveMinutes,
              }
            );
            sentryService.endSentrySession('abnormal');
            sentryService.startSentrySession();
          }
          tabHiddenTimestamp.value = null;
        }
      } catch (error) {
        warn('[App] Visibility change handler error:', messageOf(error));
        if (document.visibilityState !== 'hidden') {
          tabHiddenTimestamp.value = null;
        }
      }
    };
    document.addEventListener(
      'visibilitychange',
      boundHandlers.visibilityChange
    );

    ui.initialize();
    animations.initialize();
    kioskHardware.initialize();

    if (router.isStepPage) {
      initializeKioskServices();
    } else {
      try {
        sentryService.endSentrySession('ok');
        sentryService.setTag('kiosk_session_id', undefined);
      } catch {
        trackInitializationComplete();
        return;
      }
    }

    trackInitializationComplete();
  }

  function trackInitializationComplete(): void {
    try {
      sentryService.addBreadcrumb('app', 'App.vue initialization complete', {
        sessionId: store.sessionId || null,
      });
    } catch (error) {
      warn('[Sentry] App init complete breadcrumb failed:', messageOf(error));
    }
  }

  function cleanupApp(): void {
    const cleanupSteps: [label: string, step: () => void][] = [
      [
        'beforeUnload cleanup',
        (): void => {
          if (boundHandlers.beforeUnload) {
            window.removeEventListener(
              'beforeunload',
              boundHandlers.beforeUnload
            );
          }
        },
      ],
      [
        'pagehide cleanup',
        (): void => {
          if (boundHandlers.pagehide) {
            window.removeEventListener('pagehide', boundHandlers.pagehide);
          }
        },
      ],
      [
        'visibilitychange cleanup',
        (): void => {
          if (boundHandlers.visibilityChange) {
            document.removeEventListener(
              'visibilitychange',
              boundHandlers.visibilityChange
            );
          }
        },
      ],
      [
        'locale handler cleanup',
        (): void => {
          if (boundHandlers.localeChanged) {
            router.off(
              router.EVENTS.LOCALE_CHANGED,
              boundHandlers.localeChanged
            );
          }
        },
      ],
      ['IdleWatcher cleanup', (): void => idleWatcher.cleanup()],
      [
        'KioskConfig refresh cleanup',
        (): void => kioskConfig.stopAutoRefresh(),
      ],
      ['KioskHardware destroy', (): void => kioskHardware.destroy()],
    ];

    for (const [label, step] of cleanupSteps) {
      try {
        step();
      } catch (error) {
        warn(`[App] ${label} failed:`, messageOf(error));
      }
    }

    try {
      sentryService.addBreadcrumb('app', 'App.vue unmounting', {
        sessionId: store.sessionId || null,
        hasActiveSession: Boolean(session.id),
      });

      if (session.id) {
        sentryService.endSentrySession('abnormal');
      }
    } catch (error) {
      warn('[Sentry] App unmount breadcrumb failed:', messageOf(error));
    }
  }

  return {
    boundHandlers,
    cleanupApp,
    initializeApp,
    tabHiddenTimestamp,
    ui,
  };
}
